// src/pages/ContactPage.tsx

import React, { FC, useState } from 'react';
import styles from '../css/ContactPage.module.css';
import { motion, Variants } from 'framer-motion';
import { 
    MapPin, 
    LifeBuoy, 
    ArrowRight, 
    Send, 
    Mail,
    Building2
} from 'lucide-react';

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';

// --- Interface Definitions ---
interface FormData {
    name: string;
    email: string;
    inquiryType: string;
    message: string;
}

const ContactPage: FC = () => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        inquiryType: 'general',
        message: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('Form Submitted:', formData);
        alert('Transmission received. Our team will respond shortly.');
        setFormData({ name: '', email: '', inquiryType: 'general', message: '' });
    };

    // Animation Variants
    const containerVariants: Variants = { 
        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } 
    };
    
    const itemVariants: Variants = { 
        hidden: { opacity: 0, y: 20 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
    };

    return (
        <div className={styles.contactPage}>

            {/* 1. HERO SECTION ("Clean Lab") */}
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    <div className={styles.heroContent}>
                        <motion.div 
                            className={styles.heroWrapper}
                            initial="hidden" animate="visible" variants={containerVariants}
                        >
                            <motion.span variants={itemVariants} className={styles.heroBadge}>
                                Communication Channels
                            </motion.span>
                            
                            <motion.h1 variants={itemVariants} className={styles.heroTitle}>
                                Corporate <br/>
                                <span className={styles.titleHighlight}>Inquiries & Liaison.</span>
                            </motion.h1>
                            
                            <motion.p variants={itemVariants} className={styles.heroSubtitle}>
                                Connect with OTA TECH Inc. for investment dossiers, media requests, and strategic partnership opportunities.
                            </motion.p>
                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>

            {/* 2. SPLIT LAYOUT */}
            <AnimatedSection className={styles.contentSection}>
                <div className={styles.container}>
                    <motion.div 
                        className={styles.contentLayout}
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        
                        {/* LEFT: CONTACT FORM ("Data Entry Terminal") */}
                        <motion.div className={styles.formWrapper} variants={itemVariants}>
                            <div className={styles.formHeader}>
                                <Mail size={20} className={styles.formIcon}/>
                                <h3>Secure Message Transmission</h3>
                            </div>
                            
                            <form onSubmit={handleSubmit} className={styles.contactForm}>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="name" className={styles.label}>Full Name / Entity</label>
                                        <input 
                                            type="text" 
                                            id="name" 
                                            name="name" 
                                            value={formData.name} 
                                            onChange={handleChange} 
                                            className={styles.input}
                                            placeholder="Enter identification..."
                                            required 
                                        />
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label htmlFor="email" className={styles.label}>Work Email</label>
                                        <input 
                                            type="email" 
                                            id="email" 
                                            name="email" 
                                            value={formData.email} 
                                            onChange={handleChange} 
                                            className={styles.input}
                                            placeholder="name@organization.com"
                                            required 
                                        />
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="inquiryType" className={styles.label}>Classification</label>
                                    <div className={styles.selectWrapper}>
                                        <select 
                                            id="inquiryType" 
                                            name="inquiryType" 
                                            value={formData.inquiryType} 
                                            onChange={handleChange}
                                            className={styles.select}
                                            required
                                        >
                                            <option value="general">General Corporate Inquiry</option>
                                            <option value="investor">Investor Relations / Series A</option>
                                            <option value="media">Media / Press Kit</option>
                                            <option value="partnership">Strategic Partnership</option>
                                        </select>
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="message" className={styles.label}>Brief</label>
                                    <textarea 
                                        id="message" 
                                        name="message" 
                                        rows={6} 
                                        value={formData.message} 
                                        onChange={handleChange} 
                                        className={styles.textarea}
                                        placeholder="Outline your proposal or query..."
                                        required 
                                    />
                                </div>

                                <button type="submit" className={styles.submitButton}>
                                    Transmit Message <Send size={16} />
                                </button>
                            </form>
                        </motion.div>

                        {/* RIGHT: INFO SIDEBAR */}
                        <motion.div className={styles.infoSidebar} variants={itemVariants}>
                            <div className={styles.sidebarHeader}>
                                <h3 className={styles.sidebarTitle}>Global Presence</h3>
                                <p className={styles.sidebarSubtitle}>Operating across key strategic hubs.</p>
                            </div>
                            
                            <div className={styles.locationCard}>
                                <div className={styles.locationHeader}>
                                    <Building2 size={18} className={styles.locIcon} />
                                    <span className={styles.locType}>Corporate HQ</span>
                                </div>
                                <div className={styles.locationBody}>
                                    <h4>Delaware, USA</h4>
                                    <p>Legal, Investment & Governance</p>
                                </div>
                            </div>

                            <div className={styles.locationCard}>
                                <div className={styles.locationHeader}>
                                    <MapPin size={18} className={styles.locIcon} />
                                    <span className={styles.locType}>Operations Hub</span>
                                </div>
                                <div className={styles.locationBody}>
                                    <h4>Accra, Ghana</h4>
                                    <p>R&D & Software Engineering</p>
                                </div>
                            </div>
                            
                            <div className={styles.supportNote}>
                                <div className={styles.noteHeader}>
                                    <LifeBuoy size={18} /> <span>Technical Support</span>
                                </div>
                                <p>
                                    Existing clients should submit tickets via the secure portal.
                                </p>
                                <a href="#" className={styles.link}>
                                    Access Product Portal <ArrowRight size={14} />
                                </a>
                            </div>
                        </motion.div>

                    </motion.div>
                </div>
            </AnimatedSection>
        </div>
    );
};

export default ContactPage;